import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useKycStore } from '@store/useKycStore'
import { useLocation, useNavigate } from 'react-router-dom'
import kycAPI from '@/services/api/kyc'

import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import MotionText from '@/components/common/Motion/MotionText'
import Input from '@components/common/Input/Input'
import Button from '@/components/common/Button/Button'

import { useUserStore } from '@/store/useUserStore'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import { usePageSetup } from '@/hooks/usePageSetup'

/* TODO: This needs to be separated to a form for logged in and coming from profile */

export const KycDetailsPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('kyc.pageTitle.details'), { scroll: true })

  const location = useLocation()
  const isProfile = location.state?.profile

  const user = useUserStore((state) => state.user)
  const setUser = useUserStore((state) => state.setUser)

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(2), [])

  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)

    try {
      setIsLoading(true)

      const kycParams = {
        first_name: formData.get('firstName') as string,
        last_name: formData.get('lastName') as string,
        address: formData.get('address') as string,
        business_certificate_number: formData.get('businessCertificateNumber') as string,
      }

      const response = await kycAPI.create({ kyc: kycParams })

      user && setUser({ ...user, kyc: response.kyc })

      if (isProfile) {
        navigate("/profile")
      } else {
        navigate("/kyc/vehicle/type")
      }
    } catch (error: any) {
      setIsLoading(false)
    }
  }

  return <>
    {isProfile && <HeaderBack />}
    <form onSubmit={handleSubmit} className="h-full bg-white px-5 pb-8 flex flex-col">
      {!isProfile && <HeaderBack className='items-center gap-4 pt-5 pb-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={8}
        />
      </HeaderBack>}

      <div className="flex-1 flex flex-col">
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('kyc.details.title')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('kyc.details.description')}
          </MotionText>
        </div>
        <motion.div className='flex flex-col mt-8 mb-[7rem] gap-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            delay: 0.2,
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          <Input
            defaultValue={user?.kyc?.first_name || undefined}
            name='firstName'
            label={t('kyc.details.firstName')}
            required
          />
          <Input
            defaultValue={user?.kyc?.last_name || undefined}
            name='lastName'
            label={t('kyc.details.lastName')}
            required
          />
          <Input
            defaultValue={user?.kyc?.address || undefined}
            name='address'
            label={t('kyc.details.address')}
            required
          />
          <Input
            defaultValue={user?.kyc?.business_certificate_number || undefined}
            name='businessCertificateNumber'
            inputMode='numeric'
            label={t('kyc.details.businessCertificate')}
            optional={true}
          />
        </motion.div>
      </div>

      <div className="absolute bottom-0 ml-[-1.25rem] pl-5 pr-5 pb-8 w-full bg-linear-to-t from-white via-white to-transparent">
        <Button type='submit' isLoading={isLoading}>
          <motion.span
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeOut"
            }}
          >
            {t('common.save')}
          </motion.span>
        </Button>
      </div>
    </form>
  </>
}

export default KycDetailsPage